import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';

import type { AssessmentData } from '@features/calibration/hooks/useCalibrationRuntime/types';

type Props = {
  assessment: AssessmentData;
};

type FieldEntry = {
  label: string;
  value: string;
};

const HistoryAssessment: React.FC<Props> = ({ assessment }) => {
  const { t } = useTranslation('history');

  const fields = useMemo(() => {
    const entries: FieldEntry[] = [];

    if (assessment.conditionAsFound) {
      entries.push({
        label: `${t('assessment.asFound')} ${t('assessment.condition')}`,
        value: assessment.conditionAsFound
      });
    }

    if (assessment.conditionAsLeft) {
      entries.push({
        label: `${t('assessment.asLeft')} ${t('assessment.condition')}`,
        value: assessment.conditionAsLeft
      });
    }

    if (assessment.operabilityAsFound) {
      entries.push({
        label: `${t('assessment.asFound')} ${t('assessment.operability')}`,
        value: assessment.operabilityAsFound
      });
    }

    if (assessment.operabilityAsLeft) {
      entries.push({
        label: `${t('assessment.asLeft')} ${t('assessment.operability')}`,
        value: assessment.operabilityAsLeft
      });
    }

    return entries;
  }, [assessment, t]);

  const hasNotes = !!assessment.inspectionNotes;

  if (fields.length === 0 && !hasNotes) return null;

  return (
    <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {t('assessment.title')}
      </Typography>

      {fields.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: hasNotes ? 2 : 0
          }}
        >
          {fields.map(field => (
            <Box key={field.label}>
              <Typography variant="caption" color="text.secondary">
                {field.label}
              </Typography>
              <Typography variant="body2">{field.value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {hasNotes && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('assessment.inspectionNotes')}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {assessment.inspectionNotes}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default HistoryAssessment;
